import { dateUStoISO } from './date-converter';

let cloneDeep = require('lodash.clonedeep');

function fromLocaleString(str) {
	const num = Number(str.replace(/[\s,$]/g,''));
	return num === num ? num : null;
}

export const propertyOrder = [
    'SOURCE',
    'STATUS',
    'HSC_STATUS',
    'VISIBLE',
    'FEATURED',
    'NAME',
    'ADDRESS',
    'CITY',
    'STATE',
    'ZIP',
    'LATITUDE',
    'LONGITUDE',
    'HIGHLIGHTS',
    'MEDIA_KIT',
    'TYPE',
    'SQFT',
    'LIST_PRICE',
    'NET_OPERATING_INCOME',
    'VACANCY',
    'CAPITALIZATION_RATE',
    'SOLD_PRICE',
    'DOWN_PAYMENT',
    'EFFECTIVE_DATE',
    'CLOSE_OF_ESCROW',
    'LISTED_DATE',
    'SELLER_FEE_PERCENTAGE',
    'LEASE_TYPE',
    'LEASE_COMMENCEMENT_DATE',
    'LEASE_EXPIRATION_DATE',
    'SELLER_ID',
    'BUYER_ID',
    'YEAR_BUILT',
    'VIDEO_URL',
    'FLIP_BOOK',
    'CLOSE_CAP_RATE',
    'CLOSE_NOI',
    'CLOSE_PRICE_PER_SQFT',
    'PRICE_PER_SQFT',
    'LISTING_AGENT_1',
    'LISTING_AGENT_2',
    'LISTING_AGENT_3',
    'LISTING_AGENT_4',
    'LISTING_AGENT_4_FIRM',
    'BUYER_AGENT_1',
    'BUYER_AGENT_2',
    'BUYER_AGENT_3',
    'BUYER_AGENT_4',
    'BUYER_AGENT_4_FIRM',
    'SINGLE_TENANT_SUBCATEGORY',
    'MULTI_TENANT_SUBCATEGORY',
    'TENANT_ID',
    'MARKET',
    'RENT_PER_SQFT',
    'AGENT_NULL_FLAG', // sort-only placeholder: non-null when AGENT_1.LAST_NAME is null
    'AGENT_1_LAST_NAME',
    'NEGOTIATION_PRICE',
    'NEGOTIATION_CAP_RATE',
    'CATEGORY',
    'SELLERS_LAST_NAME',
    'HSC_PRICE',
    'LOAN_AMOUNT',
    'LTV',
    'INTEREST_RATE',
    'AMORTIZATION',
    'TERM',
    'LENDER_NAME',
    'COUNTY',
    'CURRENT_OWNER_NULL_FLAG',
    'CURRENT_OWNER_NAME',
    'ARCIMEDES_ROW_ID', // leave this as last item
    // Appended after ARCIMEDES_ROW_ID so existing order slots don't shift; must
    // stay index-aligned with backend PROPERTY_ORDER_COLUMNS.
    'COURTING_SINCE',
    'MATURITY_DATE',
    'STATUS_NULL_FLAG', // sort-only placeholder: non-null when STATUS is blank
    'STATUS_FLAG_NONE', // sort-only placeholder: non-null when the row has no status flag
    'STATUS_FLAG_RANK', // sort-only placeholder: the flag's place in the client's order
];

export default (query = {}, page = 'properties') => {
	const where = [];
	const param = {};
	const order = [];

    // NOTE: the order of these properties must match the order in the PROPERTIES query (/src/data/queries.sql)
    const propertyFilter = [
        id => match(id, 'NAME'),
        id => match(id, 'ADDRESS'),
        id => multi(id, 'ZIP', 120),
        id => match(id, 'CITY'),
        id => multi(id, 'STATE', 6),
        id => exact(id, 'TYPE'),
        id => multi(id, 'CATEGORY', 5),
        id => exact(id, 'SOURCE'),
        id => exact(id, 'TRANSACTION_TYPE'),
        id => multi(id, 'MARKET', 9),
        id => exact(id, 'SINGLE_TENANT_SUBCATEGORY'),
        id => exact(id, 'LEASE_TYPE'),
        id => exact(id, 'MULTI_TENANT_SUBCATEGORY'),
        id => exact(id, 'LISTING_AGENT_4'),
        id => exact(id, 'LISTING_AGENT_4_FIRM'),
        id => exact(id, 'BUYER_AGENT_4'),
        id => exact(id, 'BUYER_AGENT_4_FIRM'),
        id => num(id, 'SQFT_MIN'),
        id => num(id, 'SQFT_MAX'),
        id => num(id, 'LIST_PRICE_MIN'),
        id => num(id, 'LIST_PRICE_MAX'),
        id => num(id, 'NET_OPERATING_INCOME_MIN'),
        id => num(id, 'NET_OPERATING_INCOME_MAX'),
        id => num(id, 'SOLD_PRICE_MIN'),
        id => num(id, 'SOLD_PRICE_MAX'),
        id => num(id, 'CAPITALIZATION_RATE_MIN'),
        id => num(id, 'CAPITALIZATION_RATE_MAX'),
        id => num(id, 'PRICE_PER_SQFT_MIN'),
        id => num(id, 'PRICE_PER_SQFT_MAX'),
        id => num(id, 'CLOSE_PRICE_PER_SQFT_MIN'),
        id => num(id, 'CLOSE_PRICE_PER_SQFT_MAX'),
        id => remaingTime(id, 'LEASE_EXPIRATION_DATE_MIN'),
        id => remaingTime(id, 'LEASE_EXPIRATION_DATE_MAX'),
        id => exact(id, 'BUYER_COMPANY_STATE'),
        id => exact(id, 'SELLER_COMPANY_STATE'),
        id => multi(id, 'STATUS', 8),
        id => multi(id, 'HSC_STATUS', 4),
        id => exact(id, 'LISTING_AGENT_TEAM'),
        id => date(id, 'CLOSE_OF_ESCROW_MIN'),
        id => date(id, 'CLOSE_OF_ESCROW_MAX'),
        id => num(id, 'CLOSE_CAP_RATE_MIN'),
        id => num(id, 'CLOSE_CAP_RATE_MAX'),
        id => date(id, 'LISTED_DATE_MIN'),
        id => date(id, 'LISTED_DATE_MAX'),
        id => num(id, 'OCCUPANCY_MIN'),
        id => num(id, 'OCCUPANCY_MAX'),
        id => num(id, 'YEAR_BUILT_MIN'),
        id => num(id, 'YEAR_BUILT_MAX'),
        id => date(id, 'DATE_CREATED_MIN'),
        id => date(id, 'DATE_CREATED_MAX'),
        id => exact(id, 'CREATED_BY'),
        id => date(id, 'LAST_MODIFIED_MIN'),
        id => date(id, 'LAST_MODIFIED_MAX'),
        id => exact(id, 'MODIFIED_BY'),
        id => exact(id, 'FEATURED'),
        id => exact(id, 'VISIBLE'),
        id => num(id, 'LOT_SIZE_MIN'),
        id => num(id, 'LOT_SIZE_MAX'),
    ];

    // Buyer-breakdown SQL (PROPERTIES_BY_BUYER_STATE) has a DIFFERENT WHERE clause
    // order than V7_PROPERTIES_LIST. Verified against actual backend SQL 2026-04-02.
    // Identical to V7 through CW44 (LISTED_DATE_MAX), then has FEATURED_DATE_MIN/MAX,
    // then continues with OCCUPANCY through LOT_SIZE, then BASE/COMPARISON dates.
    const buyerBreakdownFilter = [
        id => match(id, 'NAME'),                        // CW1  - NAME
        id => match(id, 'ADDRESS'),                     // CW2  - ADDRESS
        id => multi(id, 'ZIP', 120),                    // CW3  - ZIP (multi, 120)
        id => match(id, 'CITY'),                        // CW4  - CITY (like)
        id => exact(id, 'STATE'),                       // CW5  - STATE (single)
        id => exact(id, 'TYPE'),                        // CW6  - TYPE
        id => multi(id, 'CATEGORY', 5),                 // CW7  - CATEGORY (multi, 5)
        id => exact(id, 'SOURCE'),                      // CW8  - SOURCE
        id => exact(id, 'TRANSACTION_TYPE'),            // CW9  - TRANSACTION_TYPE
        id => multi(id, 'MARKET', 9),                   // CW10 - MARKET
        id => exact(id, 'SINGLE_TENANT_SUBCATEGORY'),   // CW11 - SINGLE_TENANT_SUBCATEGORY
        id => exact(id, 'LEASE_TYPE'),                  // CW12 - LEASE_TYPE
        id => exact(id, 'MULTI_TENANT_SUBCATEGORY'),    // CW13 - MULTI_TENANT_SUBCATEGORY
        id => exact(id, 'LISTING_AGENT_4'),             // CW14 - LISTING_AGENT_4
        id => exact(id, 'LISTING_AGENT_4_FIRM'),        // CW15 - LISTING_AGENT_4_FIRM
        id => exact(id, 'BUYER_AGENT_4'),               // CW16 - BUYER_AGENT_4
        id => exact(id, 'BUYER_AGENT_4_FIRM'),          // CW17 - BUYER_AGENT_4_FIRM
        id => num(id, 'SQFT_MIN'),                      // CW18 - SQFT_MIN
        id => num(id, 'SQFT_MAX'),                      // CW19 - SQFT_MAX
        id => num(id, 'LIST_PRICE_MIN'),                // CW20 - LIST_PRICE_MIN
        id => num(id, 'LIST_PRICE_MAX'),                // CW21 - LIST_PRICE_MAX
        id => num(id, 'NET_OPERATING_INCOME_MIN'),      // CW22 - NOI_MIN
        id => num(id, 'NET_OPERATING_INCOME_MAX'),      // CW23 - NOI_MAX
        id => num(id, 'SOLD_PRICE_MIN'),                // CW24 - SOLD_PRICE_MIN
        id => num(id, 'SOLD_PRICE_MAX'),                // CW25 - SOLD_PRICE_MAX
        id => num(id, 'CAPITALIZATION_RATE_MIN'),       // CW26 - CAP_RATE_MIN
        id => num(id, 'CAPITALIZATION_RATE_MAX'),       // CW27 - CAP_RATE_MAX
        id => num(id, 'PRICE_PER_SQFT_MIN'),           // CW28 - PRICE_PER_SQFT_MIN
        id => num(id, 'PRICE_PER_SQFT_MAX'),           // CW29 - PRICE_PER_SQFT_MAX
        id => num(id, 'CLOSE_PRICE_PER_SQFT_MIN'),     // CW30 - CLOSE_PRICE_PER_SQFT_MIN
        id => num(id, 'CLOSE_PRICE_PER_SQFT_MAX'),     // CW31 - CLOSE_PRICE_PER_SQFT_MAX
        id => remaingTime(id, 'LEASE_EXPIRATION_DATE_MIN'), // CW32 - LEASE_EXP_MIN
        id => remaingTime(id, 'LEASE_EXPIRATION_DATE_MAX'), // CW33 - LEASE_EXP_MAX
        id => exact(id, 'BUYER_COMPANY_STATE'),         // CW34 - BUYER_COMPANY_STATE
        id => exact(id, 'SELLER_COMPANY_STATE'),        // CW35 - SELLER_COMPANY_STATE
        id => multi(id, 'STATUS', 8),                   // CW36 - STATUS
        id => multi(id, 'HSC_STATUS', 4),               // CW37 - HSC_STATUS
        id => exact(id, 'LISTING_AGENT_TEAM'),          // CW38 - LISTING_AGENT_TEAM
        id => date(id, 'CLOSE_OF_ESCROW_MIN'),          // CW39 - CLOSE_OF_ESCROW_MIN
        id => date(id, 'CLOSE_OF_ESCROW_MAX'),          // CW40 - CLOSE_OF_ESCROW_MAX
        id => num(id, 'CLOSE_CAP_RATE_MIN'),            // CW41 - CLOSE_CAP_RATE_MIN
        id => num(id, 'CLOSE_CAP_RATE_MAX'),            // CW42 - CLOSE_CAP_RATE_MAX
        id => date(id, 'LISTED_DATE_MIN'),              // CW43 - LISTED_DATE_MIN
        id => date(id, 'LISTED_DATE_MAX'),              // CW44 - LISTED_DATE_MAX
        id => date(id, 'FEATURED_DATE_MIN'),            // CW45 - FEATURED_DATE_MIN (buyer-breakdown only)
        id => date(id, 'FEATURED_DATE_MAX'),            // CW46 - FEATURED_DATE_MAX (buyer-breakdown only)
        id => num(id, 'OCCUPANCY_MIN'),                 // CW47 - OCCUPANCY_MIN
        id => num(id, 'OCCUPANCY_MAX'),                 // CW48 - OCCUPANCY_MAX
        id => num(id, 'YEAR_BUILT_MIN'),                // CW49 - YEAR_BUILT_MIN
        id => num(id, 'YEAR_BUILT_MAX'),                // CW50 - YEAR_BUILT_MAX
        id => date(id, 'DATE_CREATED_MIN'),             // CW51 - DATE_CREATED_MIN
        id => date(id, 'DATE_CREATED_MAX'),             // CW52 - DATE_CREATED_MAX
        id => exact(id, 'CREATED_BY'),                  // CW53 - CREATED_BY
        id => date(id, 'LAST_MODIFIED_MIN'),            // CW54 - LAST_MODIFIED_MIN
        id => date(id, 'LAST_MODIFIED_MAX'),            // CW55 - LAST_MODIFIED_MAX
        id => exact(id, 'MODIFIED_BY'),                 // CW56 - MODIFIED_BY
        id => exact(id, 'FEATURED'),                    // CW57 - FEATURED
        id => exact(id, 'VISIBLE'),                     // CW58 - VISIBLE
        id => num(id, 'LOT_SIZE_MIN'),                  // CW59 - LOT_SIZE_MIN
        id => num(id, 'LOT_SIZE_MAX'),                  // CW60 - LOT_SIZE_MAX
        id => date(id, 'BASE_MIN'),                     // CW61 - BASE_MIN
        id => date(id, 'BASE_MAX'),                     // CW62 - BASE_MAX
        id => date(id, 'COMPARISON_MIN'),               // CW63 - COMPARISON_MIN
        id => date(id, 'COMPARISON_MAX'),               // CW64 - COMPARISON_MAX
    ];

    // Extended filters for V7_PROPERTIES_LIST only (must match SQL order)
    propertyFilter.push(
        id => num(id, 'UNITS_MIN'),
        id => num(id, 'UNITS_MAX'),
        id => date(id, 'LOAN_CLOSING_DATE_MIN'),
        id => date(id, 'LOAN_CLOSING_DATE_MAX'),
        id => match(id, 'COUNTY'),
        // CW64 was APN (Tax Acct #). The popup searches Account # instead; kept
        // as a no-op so the slots after it keep their numbers.
        id => undefined,
        id => num(id, 'ASSESSED_VALUE_MIN'),
        id => num(id, 'ASSESSED_VALUE_MAX'),
        id => num(id, 'ASSESSED_YEAR_MIN'),
        id => num(id, 'ASSESSED_YEAR_MAX'),
        id => num(id, 'ACRES_MIN'),
        id => num(id, 'ACRES_MAX'),
        id => date(id, 'TRANS_DATE_MIN'),
        id => date(id, 'TRANS_DATE_MAX'),
        id => num(id, 'TRANS_PRICE_MIN'),
        id => num(id, 'TRANS_PRICE_MAX'),
        id => exact(id, 'CORE_PROPERTY'),
        id => match(id, 'LEGAL_DESC'),
        id => match(id, 'NOTES'),
        id => exact(id, 'BUYER_ID'),
        id => exact(id, 'SELLER_ID'),
        id => exact(id, 'CURRENT_OWNER_ID'),
    );
    propertyFilter.push(    
        id => exact(id, 'EMPTY')
    );

    // NOTE: the order of these properties must match the order in the TEXAS_TOTAL_TRANSACTIONS,
    // TEXAS_REGION_TRANSACTIONS, BUYERS_LOCATION_TOTAL, BUYERS_LOCATION queries (/src/data/queries.sql)
    const transactionBreakdownFilter = [
        id => exact(id, 'STATE'),
        id => exact(id, 'TYPE'),
        id => exact(id, 'CATEGORY'),
        id => exact(id, 'STATUS'),
        id => num(id, 'TRANSACTION_YEAR'),
        id => num(id, 'SOLD_PRICE_MIN'),
        id => num(id, 'SOLD_PRICE_MAX'),
        id => num(id, 'SQFT_MIN'),
        id => num(id, 'SQFT_MAX'),
    ];

    // NOTE: the order of these properties must match the order in the TEXAS_MARKET_TRANSACTIONS query
    // (/src/data/queries.sql)
    const marketTransactionBreakdownFilter = [
        id => exact(id, 'STATE'),
        id => exact(id, 'TYPE'),
        id => exact(id, 'CATEGORY'),
        id => exact(id, 'MARKET'),
        id => exact(id, 'STATUS'),
        id => date(id, 'BASE_MIN'),
        id => date(id, 'BASE_MAX'),
        id => date(id, 'COMPARISON_MIN'),
        id => date(id, 'COMPARISON_MAX'),
    ];

    const marketShareFilter = [
        id => exact(id, 'SOURCE'),
        id => exact(id, 'STATE'),
        id => exact(id, 'TYPE'),
        id => exact(id, 'CATEGORY'),
        id => exact(id, 'MARKET'),
        id => exact(id, 'STATUS'),
    ]

    // NOTE: the order of these properties must match the order in the AGENTS_LIST query (/src/data/queries.sql)
    const agentFilter = [
        id => match(id, 'NAME'),
        id => match(id, 'EMAIL'),
        id => match(id, 'PHONE'),
        id => match(id, 'CELL_PHONE'),
        id => exact(id, 'FIRM'),
        id => exact(id, 'LEAD_ID'),
    ];

    const vendorOrder = [   
        'VENDOR_TYPE',
        'FIRST_NAME',
        'LAST_NAME',
        'NAME',
        'PHONE',
        'EMAIL_ID',
        'COMPANY_NAME',
        'COMPANY_ADDRESS',
        'COMPANY_CITY',
        'COMPANY_STATE',
        'COMPANY_ZIP',
        'WEBSITE',
        'NOTES',
        'ARCIMEDES_ROW_ID',
    ];
    const vendorFilter = [
        id => match(id, 'NAME'),
        id => match(id, 'COMPANY_NAME'),
        id => match(id, 'VENDOR_TYPE'),
        id => exact(id, 'LEAD_ID'),
        id => exact(id, 'VENDOR_SUBTYPE'),
    ];

    // NOTE: the order of these properties must match the order in the SELLERS_LIST query
    // (/src/data/queries.sql)
    const sellerFilter = [
        id => match(id, 'FIRST_NAME'),
        id => match(id, 'LAST_NAME'),
        id => match(id, 'EMAIL'),
        id => match(id, 'PHONE'),    
        id => exact(id, 'STATUS'),    
        id => exact(id, 'PROFILE'),
        id => match(id, 'NOTES'),
        id => match(id, 'COMPANY_NAME'),
        id => match(id, 'COMPANY_ADDRESS'),
        id => match(id, 'COMPANY_CITY'),
        id => exact(id, 'COMPANY_STATE'),
        id => match(id, 'COMPANY_ZIP'),
    ];

    // NOTE: the order of these properties must match the order in the BUYERS_LIST query
    // (/src/data/queries.sql)
    const buyerFilter = [
        id => match(id, 'FIRST_NAME'),
        id => match(id, 'LAST_NAME'),
        id => match(id, 'EMAIL'),
        id => match(id, 'PHONE'),
        id => exact(id, 'PROFILE'),
        id => multi(id, 'STATUS', 2),
        id => exact(id, 'SINGLE_TENANT_SUBCATEGORY'),
        id => exact(id, 'MULTI_TENANT_SUBCATEGORY'),
        id => exact(id, 'PRICE_MIN'),
        id => exact(id, 'PRICE_MAX'),
        id => exact(id, 'CAP_RATE_MIN'),
        id => exact(id, 'CAP_RATE_MAX'),
        id => exact(id, 'STRIVE_AGENT'),
        id => match(id, 'NOTES'),
        id => match(id, 'COMPANY_NAME'),
        id => match(id, 'COMPANY_ADDRESS'),
        id => match(id, 'COMPANY_CITY'),
        id => exact(id, 'COMPANY_STATE'),
        id => match(id, 'COMPANY_ZIP'),
    ];

    // NOTE: the order of these properties must match the order in the FIRMS_LIST query (/src/data/queries.sql)
    const firmFilter = [
        id => match(id, 'NAME'),
        id => match(id, 'ADDRESS'),
        id => match(id, 'CITY'),
        id => exact(id, 'STATE'),
        id => match(id, 'ZIP'),
        id => match(id, 'PHONE'),
        id => exact(id, 'PARENT_FIRM'),
        id => match(id, 'WEBSITE'),
        // One box over name and address (CW9), appended last so every slot
        // above keeps the CW number it already had.
        id => match(id, 'SEARCH'),
        id => exact(id, 'CRM_ID'),
    ];

    // NOTE: the order in the TENANT_LIST query (/src/data/queries.sql)
    const tenantFilter = [
        id => match(id, 'NAME'),
    ];

    // NOTE: the order of these properties must match the order in the LENDER_LIST query (/src/data/queries.sql)
    const lenderFilter = [
        id => match(id, 'NAME'),
        id => match(id,'CITY'),
        id => match(id,'STATE'),
        id => match(id, 'CONTACT_NAME'),
        id => match(id, 'CONTACT_PHONE'),
        id => match(id, 'CONTACT_EMAIL'),

    ];

    const underwritingMTFilter = [
        id => exact(id, 'PROPERTY_ID'),
    ];

    // NOTE: the order in the STRIVE_FEES query (/src/data/queries.sql)
    const striveFeesFilter = [
        id => multi(id, 'SOURCE', 2),
        id => date(id, 'START_DATE'),
        id => date(id, 'END_DATE'),
        id => multi(id, 'TRANSACTION_TYPE', 6),   
        id => exact(id, 'CATEGORY'),
        id => exact(id, 'TYPE'),
        id => exact(id, 'SIDE'),
        id => multi(id, 'AGENT_ID', 4),
    ];

    const hscPipelineFeesFilter = [
        id => exact(id, 'HSC_STATUS'),
        id => exact(id, 'YEAR'),
    ];

    const pipelinePropertiesFilter = [
        id => exact(id, 'CLOSE_OF_ESCROW'),
        id => exact(id, 'LIST_PRICE'),
        id => exact(id, 'SOLD_PRICE'),
        id => multi(id, 'STATUS', 2),
        id => multi(id, 'TRANSACTION_TYPE', 3),
        id => exact(id, 'BUYER_AGENT_4_FIRM', 6),
    ];

    const pipelinePropertiesV2 = [
        id => exact(id, 'LIST_PRICE'),
        id => exact(id, 'SOLD_PRICE'),
        id => exact(id, 'STATUS', 1),
        id => exact(id, 'CLOSE_OF_ESCROW', 4),
        id => exact(id, 'BUYER_AGENT_4_FIRM', 5),
    ];

    // NOTE: the order of these properties must match the order in the AGENT_COMMISSION_PROCURED and AGENT_COMMISSION_PAID queries (/src/data/queries.sql)
    const agentCommissionFilter = [
        id => exact(id, 'YEAR'),
        id => exact(id, 'AGENT_ID'),
        id => exact(id, 'BUYER_AGENT_4_FIRM', 1),
    ];
    
    const agentCommissionBreakDownFilter = [
        id => exact(id, 'YEAR'),
    ];

    // NOTE: the order of these properties must match the order in the V9_PROPERTIES_LIST,V9_PROPERTIES_STATS_ACTIVE ,V9_PROPERTIES_STATS_COUNT and V9_PROPERTIES_STATS queries (/src/data/queries.sql)
    const territoryFilter = [
        id => exact(id, 'TERRITORY_LISTING_AGENT_TEAM'),
        id => multi(id, 'TYPE', 2),
        id => multi(id, 'CATEGORY', 5),
        // CW4 — product-type filter DISABLED. It matched via listing-agent → assigned
        // territory → product type, which connects to no properties. Product type now
        // filters through the derived TYPE (CW2) / CATEGORY (CW3) columns instead.
        // Kept as a no-op to preserve CW slot numbering for the filters below.
        id => undefined,
        id => regexp(id, 'TERRITORY_MARKET'),
        id => regexp(id, 'TERRITORY'),
        id => regexp(id, 'TERRITORY_ZIP'),
        id => match(id, 'NAME'),
        id => match(id, 'ADDRESS'),
        id => exact(id, 'ZIP'),
        id => match(id, 'CITY'),
        id => multi(id, 'STATE', 6),
        id => regexp(id, 'MARKET'),
        id => multi(id, 'STATUS', 8),
        id => exact(id, 'SOURCE'),
        id => exact(id, 'TRANSACTION_TYPE'),
        id => exact(id, 'SINGLE_TENANT_SUBCATEGORY'),
        id => exact(id, 'MULTI_TENANT_SUBCATEGORY'),
        id => exact(id, 'LEASE_TYPE'),

        id => num(id, 'SQFT_MIN'),
        id => num(id, 'SQFT_MAX'),
        id => num(id, 'LIST_PRICE_MIN'),
        id => num(id, 'LIST_PRICE_MAX'),
        id => num(id, 'CAPITALIZATION_RATE_MIN'),
        id => num(id, 'CAPITALIZATION_RATE_MAX'),
        id => num(id, 'PRICE_PER_SQFT_MIN'),
        id => num(id, 'PRICE_PER_SQFT_MAX'),
        id => num(id, 'SOLD_PRICE_MIN'),
        id => num(id, 'SOLD_PRICE_MAX'),
        id => num(id, 'CLOSE_CAP_RATE_MIN'),
        id => num(id, 'CLOSE_CAP_RATE_MAX'),
        id => num(id, 'CLOSE_PRICE_PER_SQFT_MIN'),
        id => num(id, 'CLOSE_PRICE_PER_SQFT_MAX'),
        id => num(id, 'NET_OPERATING_INCOME_MIN'),
        id => num(id, 'NET_OPERATING_INCOME_MAX'),
        id => num(id, 'OCCUPANCY_MIN'),
        id => num(id, 'OCCUPANCY_MAX'),
        id => remaingTime(id, 'LEASE_EXPIRATION_DATE_MIN'),
        id => remaingTime(id, 'LEASE_EXPIRATION_DATE_MAX'),
        id => date(id, 'CLOSE_OF_ESCROW_MIN'),
        id => date(id, 'CLOSE_OF_ESCROW_MAX'),
        id => date(id, 'LISTED_DATE_MIN'),
        id => date(id, 'LISTED_DATE_MAX'),


        id => num(id, 'YEAR_BUILT_MIN'),
        id => num(id, 'YEAR_BUILT_MAX'),
        id => exact(id, 'CURRENT_OWNER_ID'),
        id => exact(id, 'EMPTY'),
    ];

    const agentCommissionOrder = [
        'CLOSE_OF_ESCROW',
        'ARCIMEDES_ROW_ID', // leave this as last item
    ];

    const pricingAnalysisFilter = [
        id => exact(id, 'AGENT_EMAIL'),
    ]

    const pricingAnalysisOrder = [
        'ARCIMEDES_ROW_ID', // leave this as last item
    ];

    const propertyHistoryOrder = [
        'DATE',
        'STATUS',
        'LISTING_AGENT_1_FIRST_NAME',
        'LISTING_AGENT_FIRM_NAME',
        'PRICE',
        'CAP_RATE',
        'NET_OPERATING_INCOME',
        'PRICE_PER_SQFT',
        'BASE_RENT',
        'RENT_PER_SQFT',
        'VACANCY',
        'BUYER_FIRST_NAME',
        'SELLER_FIRST_NAME',
        'ARCIMEDES_ROW_ID', // leave this as last item
    ];

    const buyerBreakdownOrder = [
        'TOTAL_PROPERTIES',
        'COMPANY_STATE',
        'ARCIMEDES_ROW_ID', // leave this as last item
    ];

    const defaultOrder = [
        'ARCIMEDES_ROW_ID', // leave this as last item
    ];

    const agentOrder = [
        'NAME',
        'EMAIL',
        'PHONE',
        'CELL_PHONE',
        'LEAD_ID',
        'FIRM_NAME',
        'ARCIMEDES_ROW_ID', // leave this as last item
    ];

    const sellerOrder = [
        'STATUS',
        'FIRST_NAME',
        'LAST_NAME',
        'EMAIL',
        'PHONE',
        'COMPANY_NAME',
        'ARCIMEDES_ROW_ID', // leave this as last item
    ];

    const buyerOrder = [
        'STATUS',
        'CATEGORY',
        'TYPE',
        'SINGLE_TENANT_SUBCATEGORY',
        'MULTI_TENANT_SUBCATEGORY',
        'MARKET',
        'PRICE_MIN',
        'PRICE_MAX',
        'CAP_RATE_MIN',
        'CAP_RATE_MAX',
        'FIRST_NAME',
        'LAST_NAME',
        'EMAIL',
        'PHONE',
        'COMPANY_NAME',
        'STRIVE_AGENT_LAST_NAME',
        'ARCIMEDES_ROW_ID', // leave this as last item
    ];

    const firmOrder = [
        'NAME',
        'ADDRESS',
        'CITY',
        'STATE',
        'PHONE',
        'WEBSITE',
        'ARCIMEDES_ROW_ID', // leave this as last item
    ];

    const tenantOrder = [
        'NAME',
        'ARCIMEDES_ROW_ID', // leave this as last item
    ];

    const lenderOrder = [
        'NAME',
        'CITY',
        'STATE',
        'CONTACT_NAME',
        'CONTACT_PHONE',
        'CONTACT_EMAIL',
        'ARCIMEDES_ROW_ID', // leave this as last item
    ];

    const underwritingMTOrder = [
        'TENANT_ROW_ID',
        'RR_SUITE_CODE',
    ];

    const striveFeesOrder = [
        'CLOSE_OF_ESCROW',
        'ARCIMEDES_ROW_ID', // leave this as last item
    ];

    const hscPipelineFeesOrder = [
        'LOAN_CLOSING_DATE',
        'ARCIMEDES_ROW_ID', // leave this as last item
    ];
    const teamMemberOrder = [
        'IS_AGENT',
        'LAST_NAME',
        'PHONE',
        'CELL_PHONE',
        'STARTING_DATE',
        'YRS_AT_STRIVE',
        'BIRTH_DATE',
        'ON_PHONES',
        'IN_POOL', 
        'FIRST_LISTING',
        'FIRST_CLOSING',
        'ADDRESS',
        'CITY',
        'STATE',
        'ZIP',
        'ARCIMEDES_ROW_ID', // leave this as last item
    ];
    const teamMemberFilter = [
        id => match(id, 'IS_AGENT'),
        id => date(id, 'STARTING_DATE_MIN'),
        id => date(id, 'STARTING_DATE_MAX'),
        id => exact(id, 'ARCIMEDES_ROW_ID')
    ];

    const investorOrder = [
        'NAME',
        'EMAIL',
        'CELL',
        'PHONE',
        'ADDRESS',
        'CITY',
        'STATE',
        'ZIP',
        'LEAD_ID',
        'FIRM_NAME',
        'ARCIMEDES_ROW_ID', // leave this as last item
    ];
    const investorFilter = [
        id => match(id, 'NAME'),
        id => match(id, 'EMAIL'),
        id => match(id, 'PHONE'),
        id => match(id, 'CELL'),
        id => match(id, 'LEAD_ID'),
        id => exact(id, 'FIRM_ID'),
        id => match(id, 'FIRM_NAME'),
        // One box over name, firm and either party's address (CW9). Appended
        // last so every slot above keeps the CW number it already had.
        id => match(id, 'SEARCH'),
    ];

	function match(id, key) {
		if (!query[key]) return;
		// Special-case CITY for properties/territories:
		// The V7/territory queries use CITY_1..CITY_5 params (not {param:CITY}) and the city filter is expected
		// to be controlled by the standard CITY slot in the filter order (CW4). So we populate CITY_* here and push CW4.
		// buyer-breakdown uses standard {param:CITY} at CW4 and has CITY_1..5 at CW80 (handled by tenant block).
		if (key === 'CITY' && ['properties', 'territories'].includes(page)) {
			const raw = String(query[key] || '').trim();
			// Some queries / conditional fragments still reference {param:CITY}. Keep it populated too.
			param.CITY = `%${raw}%`;
			const baseCity = raw;
			const cityVariants = [
				baseCity,
				baseCity ? baseCity.toLowerCase() : '',
				baseCity ? baseCity.toUpperCase() : '',
			].filter(Boolean);
			for (let i = 0; i < 5; i++) {
				const city = cityVariants[i] || '';
				if (city) {
					param[`CITY_${i + 1}`] = city;
					param[`CITY_${i + 1}_FIRST`] = `${city}%`;
					param[`CITY_${i + 1}_LAST`] = `%${city}`;
					param[`CITY_${i + 1}_MID`] = `%${city}%`;
				} else {
					// wildcard slot so the OR-chain can still evaluate true
					param[`CITY_${i + 1}`] = '';
					param[`CITY_${i + 1}_FIRST`] = '';
					param[`CITY_${i + 1}_LAST`] = '';
					param[`CITY_${i + 1}_MID`] = '%';
				}
			}
			where.push(id);
			return;
		}

		param[key] = `%${query[key]}%`;
		where.push(id);
	}

	function exact(id, key) {
		if (!query[key]) return;

		param[key] = query[key];
		where.push(id);
	}

	function num(id, key) {
		if (!query[key]) return;

		const num = fromLocaleString(query[key]);
		if (num !== null) {
			param[key] = num;
			where.push(id);
		}
	}

	function remaingTime(id, key) {
		if (!query[key]) return;

		const num = Number(query[key]);
		if (num === num) {
			const d = new Date();
			const years = Math.floor(num);
			const months = Math.floor(12 * (num - years));
			const days = Math.round(30 * ((12 * (num - years)) - months));
			d.setUTCFullYear(d.getUTCFullYear()+years);
			d.setUTCMonth(d.getUTCMonth()+months);
			d.setUTCDate(d.getUTCDate()+days);
			param[key] = d.toISOString().slice(0,10);
			where.push(id);
		}
	}

	function date(id, key) {
		if (!query[key]) return;

		let date = dateUStoISO(query[key]);

        // work around for 7670
        if(key == 'CLOSE_OF_ESCROW_MAX'){
            date = date + ' 23:59'
        }else if(key == 'END_DATE' && page == 'striveFees'){
            date = date + ' 23:59'
        }
		if (date !== false) {
			param[key] = date;
			where.push(id);
		}
	}

	function multi(id, key, count = 5) {
		if (query[key]) {
			var items = query[key].split(',');
			for (var i = 0; i < count; i++) {
				param[key+`_${i+1}`] = items[i] || 'NOMATCH'; // garbage value that will fail match
			}
			where.push(id);
		}
	}

	function regexp(id, key) {
		if (query[key]) {
			param[key] = '^(' + query[key].split(',').join('|') + ')$';
			where.push(id);
		}
	}

    let id = 1;
    let filter = [];
    let orderCols = [];
    switch (page) {
        case 'properties':
            orderCols = propertyOrder;
            filter = propertyFilter;
            break;
        case 'buyer-breakdown':
            orderCols = buyerBreakdownOrder;
            filter = buyerBreakdownFilter;
            break;
        case 'transaction-breakdown':
            orderCols = defaultOrder;
            filter = transactionBreakdownFilter;
            break;
        case 'market-transaction-breakdown':
            orderCols = defaultOrder;
            filter = marketTransactionBreakdownFilter;
            break;
        case 'market-share':
            orderCols = defaultOrder;
            filter = marketShareFilter;
            break;
        case 'sellers':
            orderCols = sellerOrder;
            filter = sellerFilter;
            break;
        case 'buyers':
            orderCols = buyerOrder;
            filter = buyerFilter;
            break;
        case 'agents':
            orderCols = agentOrder;
            filter = agentFilter;
            break;
        case 'firms':
            orderCols = firmOrder;
            filter = firmFilter;
            break;
        case 'tenants':
            orderCols = tenantOrder;
            filter = tenantFilter;
            break;
        case 'lenders':
            orderCols = lenderOrder;
            filter = lenderFilter;
            break;
        case 'statistics':
            orderCols = defaultOrder;
            break;
        case 'underwritingMT':
            orderCols = underwritingMTOrder;
            filter = underwritingMTFilter;
            break;
        case 'striveFees':
            orderCols = striveFeesOrder;
            filter = striveFeesFilter;
            break;
        case 'hscPipeline':
            orderCols = [];
            filter = [];
            break;
        case 'hscPipelineFees':
            orderCols = hscPipelineFeesOrder;
            filter = hscPipelineFeesFilter;
            break;
        case 'history':
            orderCols = propertyHistoryOrder;
            break;
        case 'strivePipelineByStatus':
            orderCols = [];
            filter = pipelinePropertiesFilter;
            break;
        case 'strivePipeline':
        case 'strivePipelineSplit':
            orderCols = [];
            filter = pipelinePropertiesV2;
            break;
        case 'agent-commission':
            orderCols = agentCommissionOrder;
            filter = agentCommissionFilter;
            break;
        case 'agent-commission-break-down':
            orderCols = agentCommissionOrder;
            filter = agentCommissionBreakDownFilter;
            break;
        case 'team-member':
            orderCols = teamMemberOrder;
            filter = teamMemberFilter;
            break;
        case 'vendor':
            orderCols = vendorOrder;
            filter = vendorFilter;
            break;
        case 'territories':
            orderCols = propertyOrder;
            filter = territoryFilter;
            break;
        case 'pricing-analysis':
            orderCols = pricingAnalysisOrder;
            filter = pricingAnalysisFilter;
            break;
        case 'investors':
            orderCols = investorOrder;
            filter = investorFilter;
            break;
    }

    filter.forEach((rule, idx) => {
        // we need to take into account the first condition - it does not use any filter: (P.SOURCE = 'HSC' or P.HSC_FINANCING = 'y')
        if ((page.includes('hscPipeline') || page.includes('strivePipelineSplit')) && id === 1) {
            where.push('CW' + id);
            id++;
        }

        rule(`CW${id}`);
        id++;
    });

    if (param.SOURCE === 'PEAK') {
        param.SOURCE = 'HSC';
    }

    if (param.FIRM_NAME === undefined || param.FIRM_NAME === null) param.FIRM_NAME = '';

    if (page === 'investors') {
        if (param.NAME === undefined || param.NAME === null) param.NAME = '';
        if (param.EMAIL === undefined || param.EMAIL === null) param.EMAIL = '';
        if (param.PHONE === undefined || param.PHONE === null) param.PHONE = '';
        if (param.CELL === undefined || param.CELL === null) param.CELL = '';
        if (param.LEAD_ID === undefined || param.LEAD_ID === null) param.LEAD_ID = '';
        if (param.FIRM_ID === undefined || param.FIRM_ID === null) param.FIRM_ID = '';
        if (param.SEARCH === undefined || param.SEARCH === null) param.SEARCH = '';
        // Arcimedes maps CW1 to the DELETED filter slot. Always shift existing slots
        // by +1 and prepend CW1 so deleted investors are excluded on every request,
        // including initial empty-search loads.
        for (let i = 0; i < where.length; i++) {
            const slot = Number(where[i].replace('CW', ''));
            where[i] = `CW${slot + 1}`;
        }
        where.unshift('CW1');
    }

   
    if (page === 'territories') {
        if (query.HOT_LIST) {
            param.HOT_LIST = 'y';
            param.HOT_LIST_USER_EMAIL = query.HOT_LIST_USER_EMAIL || '';
        } else {
            param.HOT_LIST = 'n';
            param.HOT_LIST_USER_EMAIL = '';
        }
        param.NEW_DEVELOPMENT = query.NEW_DEVELOPMENT ? 'y' : 'n';
        param.NEED_OWNER = query.NEED_OWNER ? 'y' : 'n';
        // CW68 - Researched (y/n). Explicit equality, not truthy coercion: the
        // string 'n' is truthy in JS, so `query.RESEARCHED ? 'y' : 'n'` would
        // turn a "No" selection into a "Yes" filter.
        param.RESEARCHED = (query.RESEARCHED === 'y' || query.RESEARCHED === 'n') ? query.RESEARCHED : '';
        if (!param.TERRITORY_LISTING_AGENT_TEAM) {
            param.TERRITORY_LISTING_AGENT_TEAM = '';
        }
        for (let i = 1; i <= 2; i++) {
            if (!param[`TYPE_${i}`]) {
                param[`TYPE_${i}`] = 'NOMATCH';
            }
        }
        for (let i = 1; i <= 6; i++) {
            if (!param[`STATE_${i}`]) {
                param[`STATE_${i}`] = 'NOMATCH';
            }
        }
        for (let i = 1; i <= 5; i++) {
            if (!param[`CATEGORY_${i}`]) {
                param[`CATEGORY_${i}`] = 'NOMATCH';
            }
        }
        if (!param.ZIP) {
            param.ZIP = '';
        }
        if (!param.PRODUCT_TYPE) {
            param.PRODUCT_TYPE = 'NOMATCH';
        }
        if (!param.TERRITORY_MARKET) {
            param.TERRITORY_MARKET = 'NOMATCH';
        }
        if (!param.TERRITORY) {
            param.TERRITORY = 'NOMATCH';
        }
        if (!param.TERRITORY_ZIP) {
            param.TERRITORY_ZIP = 'NOMATCH';
        }
        for (let i = 1; i <= 8; i++) {
            if (!param[`STATUS_${i}`]) {
                param[`STATUS_${i}`] = 'NOMATCH';
            }
        }
        if (!param.MARKET) {
            param.MARKET = 'NOMATCH';
        }
        if (!param.TRANSACTION_TYPE) {
            param.TRANSACTION_TYPE = '';
        }
        if (!param.CLOSE_OF_ESCROW_MIN) {
            param.CLOSE_OF_ESCROW_MIN = '';
        }
        if (!param.CLOSE_OF_ESCROW_MAX) {
            param.CLOSE_OF_ESCROW_MAX = '';
        }
        if (!param.LISTED_DATE_MIN) {
            param.LISTED_DATE_MIN = '';
        }
        if (!param.LISTED_DATE_MAX) {
            param.LISTED_DATE_MAX = '';
        }
        if (!param.CITY) {
            param.CITY = '%';
        }
        for (let i = 1; i <= 5; i++) {
            if (!param[`CITY_${i}`]) {
                param[`CITY_${i}`] = '';
            }
            if (!param[`CITY_${i}_FIRST`]) {
                param[`CITY_${i}_FIRST`] = '';
            }
            if (!param[`CITY_${i}_LAST`]) {
                param[`CITY_${i}_LAST`] = '';
            }
            if (!param[`CITY_${i}_MID`]) {
                param[`CITY_${i}_MID`] = '%';
            }
        }
        if (!param.NAME) {
            param.NAME = '';
        }
        if (!param.ADDRESS) {
            param.ADDRESS = '';
        }
        if (!param.SOURCE) {
            param.SOURCE = '';
        }
        if (!param.SINGLE_TENANT_SUBCATEGORY) {
            param.SINGLE_TENANT_SUBCATEGORY = '';
        }
        if (!param.MULTI_TENANT_SUBCATEGORY) {
            param.MULTI_TENANT_SUBCATEGORY = '';
        }
        if (!param.LEASE_TYPE) {
            param.LEASE_TYPE = '';
        }
        if (!param.SQFT_MIN) {
            param.SQFT_MIN = '';
        }
        if (!param.SQFT_MAX) {
            param.SQFT_MAX = '';
        }
        if (!param.LIST_PRICE_MIN) {
            param.LIST_PRICE_MIN = '';
        }
        if (!param.LIST_PRICE_MAX) {
            param.LIST_PRICE_MAX = '';
        }
        if (!param.CAPITALIZATION_RATE_MIN) {
            param.CAPITALIZATION_RATE_MIN = '';
        }
        if (!param.CAPITALIZATION_RATE_MAX) {
            param.CAPITALIZATION_RATE_MAX = '';
        }
        if (!param.PRICE_PER_SQFT_MIN) {
            param.PRICE_PER_SQFT_MIN = '';
        }
        if (!param.PRICE_PER_SQFT_MAX) {
            param.PRICE_PER_SQFT_MAX = '';
        }
        if (!param.SOLD_PRICE_MIN) {
            param.SOLD_PRICE_MIN = '';
        }
        if (!param.SOLD_PRICE_MAX) {
            param.SOLD_PRICE_MAX = '';
        }
        if (!param.CLOSE_CAP_RATE_MIN) {
            param.CLOSE_CAP_RATE_MIN = '';
        }
        if (!param.CLOSE_CAP_RATE_MAX) {
            param.CLOSE_CAP_RATE_MAX = '';
        }
        if (!param.CLOSE_PRICE_PER_SQFT_MIN) {
            param.CLOSE_PRICE_PER_SQFT_MIN = '';
        }
        if (!param.CLOSE_PRICE_PER_SQFT_MAX) {
            param.CLOSE_PRICE_PER_SQFT_MAX = '';
        }
        if (!param.NET_OPERATING_INCOME_MIN) {
            param.NET_OPERATING_INCOME_MIN = '';
        }
        if (!param.NET_OPERATING_INCOME_MAX) {
            param.NET_OPERATING_INCOME_MAX = '';
        }
        if (!param.OCCUPANCY_MIN) {
            param.OCCUPANCY_MIN = '';
        }
        if (!param.OCCUPANCY_MAX) {
            param.OCCUPANCY_MAX = '';
        }
        if (!param.LEASE_EXPIRATION_DATE_MIN) {
            param.LEASE_EXPIRATION_DATE_MIN = '';
        }
        if (!param.LEASE_EXPIRATION_DATE_MAX) {
            param.LEASE_EXPIRATION_DATE_MAX = '';
        }
        if (!param.YEAR_BUILT_MIN) {
            param.YEAR_BUILT_MIN = '';
        }
        if (!param.YEAR_BUILT_MAX) {
            param.YEAR_BUILT_MAX = '';
        }
        if (!param.CURRENT_OWNER_ID) {
            param.CURRENT_OWNER_ID = '';
        }
        if (!param.EMPTY) {
            param.EMPTY = '';
        }

        if (!param.FEATURED_DATE_MIN) {
             param.FEATURED_DATE_MIN = ''; 
        }
        
        if (!param.FEATURED_DATE_MAX) {
             param.FEATURED_DATE_MAX = ''; 
        }

        // CW77 on the territory queries. Read from the query like FEATURED below:
        // defaulting it to '' unconditionally sent a no-op, so the Core Property
        // filter did nothing once a territory was chosen.
        param.CORE_PROPERTY = query.CORE_PROPERTY || '';

        // CW78 on the territory queries; blank is a no-op, like CORE_PROPERTY.
        param.ACCOUNT_NUMBER = query.ACCOUNT_NUMBER ? `%${query.ACCOUNT_NUMBER}%` : '';

        // CW79 on the territory queries (CW1-CW100 are all sent); blank is a no-op.
        param.NEED_INFO = query.NEED_INFO || '';

        // MISC panel — CW69-CW76 on the territory queries. These are not part of
        // territoryFilter (its positions run CW1-CW47 and CW48-CW62 are the tenant
        // slots), so the params are filled in here, exactly like HOT_LIST and
        // RESEARCHED above. Without this the territory list ignored Created By /
        // Modified By / the date ranges entirely.
        const territoryMiscDate = (key, endOfDay = false) => {
            const iso = query[key] ? dateUStoISO(query[key]) : false;
            // DATE_CREATED and LAST_MODIFIED_DATE are timestamps, so a bare date as
            // the upper bound would drop everything touched later that same day.
            param[key] = iso === false ? '' : (endOfDay ? `${iso} 23:59:59` : iso);
        };
        territoryMiscDate('DATE_CREATED_MIN');
        territoryMiscDate('DATE_CREATED_MAX', true);
        territoryMiscDate('LAST_MODIFIED_MIN');
        territoryMiscDate('LAST_MODIFIED_MAX', true);
        param.CREATED_BY = query.CREATED_BY || '';
        param.MODIFIED_BY = query.MODIFIED_BY || '';
        param.FEATURED = query.FEATURED || '';
        param.VISIBLE = query.VISIBLE || '';

        where.length = 0;
        for (let i = 1; i <= 100; i++) {
            where.push(`CW${i}`);
        }

    }

    if (['properties', 'buyer-breakdown'].includes(page)) {
        const tenants = (query.TENANT_ID || '').split(',');
        for (let i = 0; i < 15; i++) {
            if (tenants[i]) {
                // Actual tenant filter - use the tenant value
                param[`TENANT${i + 1}`] = tenants[i];
                param[`TENANT${i + 1}_FIRST`] = `${tenants[i]},%`;
                param[`TENANT${i + 1}_LAST`] = `%,${tenants[i]}`;
                param[`TENANT${i + 1}_MID`] = `%,${tenants[i]},%`;
                where.push('CW' + id);
            } else {
                // No tenant filter - use wildcard '%' in MID pattern to match everything
                // This makes "X.TENANT_ID like '%'" TRUE for all non-NULL values
                param[`TENANT${i + 1}`] = '';
                param[`TENANT${i + 1}_FIRST`] = '';
                param[`TENANT${i + 1}_LAST`] = '';
                param[`TENANT${i + 1}_MID`] = '%';
            }
            id++;
        }

        // Reserve an ID slot to keep CW numbering stable historically.
        // City filtering for properties is handled by the standard CITY filter slot (CW4) in match().
        id++;
    }

    if (page === 'buyer-breakdown') {
        if (!param.NAME) param.NAME = '';
        if (!param.ADDRESS) param.ADDRESS = '';
        for (let i = 1; i <= 120; i++) {
            if (!param[`ZIP_${i}`]) param[`ZIP_${i}`] = 'NOMATCH';
        }
        if (!param.CITY) param.CITY = '%';
        if (!param.STATE) param.STATE = '';
        if (!param.TYPE) param.TYPE = '';
        if (!param.SOURCE) param.SOURCE = '';
        if (!param.TRANSACTION_TYPE) param.TRANSACTION_TYPE = '';
        for (let i = 1; i <= 9; i++) {
            if (!param[`MARKET_${i}`]) param[`MARKET_${i}`] = 'NOMATCH';
        }
        for (let i = 1; i <= 5; i++) {
            if (!param[`CATEGORY_${i}`]) param[`CATEGORY_${i}`] = 'NOMATCH';
        }
        if (!param.SINGLE_TENANT_SUBCATEGORY) param.SINGLE_TENANT_SUBCATEGORY = '';
        if (!param.LEASE_TYPE) param.LEASE_TYPE = '';
        if (!param.MULTI_TENANT_SUBCATEGORY) param.MULTI_TENANT_SUBCATEGORY = '';
        if (!param.LISTING_AGENT_4) param.LISTING_AGENT_4 = '';
        if (!param.LISTING_AGENT_4_FIRM) param.LISTING_AGENT_4_FIRM = '';
        if (!param.BUYER_AGENT_4) param.BUYER_AGENT_4 = '';
        if (!param.BUYER_AGENT_4_FIRM) param.BUYER_AGENT_4_FIRM = '';
        if (!param.SQFT_MIN) param.SQFT_MIN = '';
        if (!param.SQFT_MAX) param.SQFT_MAX = '';
        if (!param.LIST_PRICE_MIN) param.LIST_PRICE_MIN = '';
        if (!param.LIST_PRICE_MAX) param.LIST_PRICE_MAX = '';
        if (!param.NET_OPERATING_INCOME_MIN) param.NET_OPERATING_INCOME_MIN = '';
        if (!param.NET_OPERATING_INCOME_MAX) param.NET_OPERATING_INCOME_MAX = '';
        if (!param.SOLD_PRICE_MIN) param.SOLD_PRICE_MIN = '';
        if (!param.SOLD_PRICE_MAX) param.SOLD_PRICE_MAX = '';
        if (!param.CAPITALIZATION_RATE_MIN) param.CAPITALIZATION_RATE_MIN = '';
        if (!param.CAPITALIZATION_RATE_MAX) param.CAPITALIZATION_RATE_MAX = '';
        if (!param.PRICE_PER_SQFT_MIN) param.PRICE_PER_SQFT_MIN = '';
        if (!param.PRICE_PER_SQFT_MAX) param.PRICE_PER_SQFT_MAX = '';
        if (!param.CLOSE_PRICE_PER_SQFT_MIN) param.CLOSE_PRICE_PER_SQFT_MIN = '';
        if (!param.CLOSE_PRICE_PER_SQFT_MAX) param.CLOSE_PRICE_PER_SQFT_MAX = '';
        if (!param.LEASE_EXPIRATION_DATE_MIN) param.LEASE_EXPIRATION_DATE_MIN = '';
        if (!param.LEASE_EXPIRATION_DATE_MAX) param.LEASE_EXPIRATION_DATE_MAX = '';
        if (!param.BUYER_COMPANY_STATE) param.BUYER_COMPANY_STATE = '';
        if (!param.SELLER_COMPANY_STATE) param.SELLER_COMPANY_STATE = '';
        for (let i = 1; i <= 8; i++) {
            if (!param[`STATUS_${i}`]) param[`STATUS_${i}`] = 'NOMATCH';
        }
        for (let i = 1; i <= 4; i++) {
            if (!param[`HSC_STATUS_${i}`]) param[`HSC_STATUS_${i}`] = 'NOMATCH';
        }
        if (!param.LISTING_AGENT_TEAM) param.LISTING_AGENT_TEAM = '';
        if (!param.CLOSE_OF_ESCROW_MIN) param.CLOSE_OF_ESCROW_MIN = '';
        if (!param.CLOSE_OF_ESCROW_MAX) param.CLOSE_OF_ESCROW_MAX = '';
        if (!param.CLOSE_CAP_RATE_MIN) param.CLOSE_CAP_RATE_MIN = '';
        if (!param.CLOSE_CAP_RATE_MAX) param.CLOSE_CAP_RATE_MAX = '';
        if (!param.LISTED_DATE_MIN) param.LISTED_DATE_MIN = '';
        if (!param.LISTED_DATE_MAX) param.LISTED_DATE_MAX = '';
        if (!param.FEATURED_DATE_MIN) param.FEATURED_DATE_MIN = '';
        if (!param.FEATURED_DATE_MAX) param.FEATURED_DATE_MAX = '';
        if (!param.OCCUPANCY_MIN) param.OCCUPANCY_MIN = '';
        if (!param.OCCUPANCY_MAX) param.OCCUPANCY_MAX = '';
        if (!param.YEAR_BUILT_MIN) param.YEAR_BUILT_MIN = '';
        if (!param.YEAR_BUILT_MAX) param.YEAR_BUILT_MAX = '';
        if (!param.DATE_CREATED_MIN) param.DATE_CREATED_MIN = '';
        if (!param.DATE_CREATED_MAX) param.DATE_CREATED_MAX = '';
        if (!param.CREATED_BY) param.CREATED_BY = '';
        if (!param.LAST_MODIFIED_MIN) param.LAST_MODIFIED_MIN = '';
        if (!param.LAST_MODIFIED_MAX) param.LAST_MODIFIED_MAX = '';
        if (!param.MODIFIED_BY) param.MODIFIED_BY = '';
        if (!param.FEATURED) param.FEATURED = '';
        if (!param.VISIBLE) param.VISIBLE = '';
        if (!param.LOT_SIZE_MIN) param.LOT_SIZE_MIN = '';
        if (!param.LOT_SIZE_MAX) param.LOT_SIZE_MAX = '';
        if (!param.BASE_MIN) param.BASE_MIN = '';
        if (!param.BASE_MAX) param.BASE_MAX = '';
        if (!param.COMPARISON_MIN) param.COMPARISON_MIN = '';
        if (!param.COMPARISON_MAX) param.COMPARISON_MAX = '';
        for (let i = 1; i <= 5; i++) {
            if (!param[`CITY_${i}`]) param[`CITY_${i}`] = '';
            if (!param[`CITY_${i}_FIRST`]) param[`CITY_${i}_FIRST`] = '';
            if (!param[`CITY_${i}_LAST`]) param[`CITY_${i}_LAST`] = '';
            if (!param[`CITY_${i}_MID`]) param[`CITY_${i}_MID`] = '%';
        }
    }

    if (page.includes('hscPipeline') || page === 'properties'){
        if(id === 1){ // only for HSC_PIPELINE query
            where.push('CW' + id);
            id++;
        }
        if(query.HSC_TEAM_MEMBERS){
            const hscAgents = (query.HSC_TEAM_MEMBERS || '').split(',');
            param[`HSC_TEAM_MEMBER_FIRST`] = hscAgents[0] ? hscAgents[0] : 'NOMATCH';
            param[`HSC_TEAM_MEMBER_LAST`] = hscAgents[1] ? hscAgents[1] : 'NOMATCH';
            // For 'properties', the HSC team slot is the fixed CW98 (V7_HSC_TEAM_SLOT).
            // For hscPipeline the slot is sequential from id (raw SQL, CW number is unused).
            where.push(page === 'properties' ? 'CW98' : 'CW' + id);
        }
        id++;

        if (query.NEW_DEVELOPMENT) {
            param.NEW_DEVELOPMENT = 'y';
            where.push('CW' + id);
        }
        id++;

        if (query.HOT_LIST) {
            param.HOT_LIST = 'y';
            param.HOT_LIST_USER_EMAIL = query.HOT_LIST_USER_EMAIL || '';
            where.push('CW' + id);
        } else {
            param.HOT_LIST_USER_EMAIL = '';
        }
        id++;

        if (query.NEED_OWNER === 'y' || query.NEED_OWNER === 'n') {
            param.NEED_OWNER = query.NEED_OWNER;
            where.push('CW' + id);
        } else {
            param.NEED_OWNER = '';
        }
        id++;

        // CW102 - drop one transaction type (Summit Closings / YTD send 'Extra Payment').
        if (query.EXCLUDE_TRANSACTION_TYPE) {
            param.EXCLUDE_TRANSACTION_TYPE = query.EXCLUDE_TRANSACTION_TYPE;
            where.push('CW' + id);
        } else {
            param.EXCLUDE_TRANSACTION_TYPE = '';
        }
        id++;

        // CW103 - Researched (y/n), same on/no-op contract as Need Owner above.
        if (query.RESEARCHED === 'y' || query.RESEARCHED === 'n') {
            param.RESEARCHED = query.RESEARCHED;
            where.push('CW' + id);
        } else {
            param.RESEARCHED = '';
        }
        id++;

        // CW104/CW105 - Peak pane Maturity Date window (V7 only).
        if (page === 'properties') {
            date('CW' + id, 'MATURITY_DATE_MIN');
            id++;
            date('CW' + id, 'MATURITY_DATE_MAX');
            id++;
            // CW106 - Account # (PROPERTY_ACCOUNT_NUMBERS, not the APN).
            match('CW' + id, 'ACCOUNT_NUMBER');
            id++;
            // CW107 - Need Info flag, same yes-flag contract as CORE_PROPERTY.
            exact('CW' + id, 'NEED_INFO');
            id++;
        }
    }

    if (page === 'properties') {
        if (!param.UNITS_MIN) param.UNITS_MIN = '';
        if (!param.UNITS_MAX) param.UNITS_MAX = '';
        if (!param.LOAN_CLOSING_DATE_MIN) param.LOAN_CLOSING_DATE_MIN = '';
        if (!param.LOAN_CLOSING_DATE_MAX) param.LOAN_CLOSING_DATE_MAX = '';
        if (!param.MATURITY_DATE_MIN) param.MATURITY_DATE_MIN = '';
        if (!param.MATURITY_DATE_MAX) param.MATURITY_DATE_MAX = '';
        if (!param.COUNTY) param.COUNTY = '%';
        if (!param.ASSESSED_VALUE_MIN) param.ASSESSED_VALUE_MIN = '';
        if (!param.ASSESSED_VALUE_MAX) param.ASSESSED_VALUE_MAX = '';
        if (!param.ASSESSED_YEAR_MIN) param.ASSESSED_YEAR_MIN = '';
        if (!param.ASSESSED_YEAR_MAX) param.ASSESSED_YEAR_MAX = '';
        if (!param.ACRES_MIN) param.ACRES_MIN = '';
        if (!param.ACRES_MAX) param.ACRES_MAX = '';
        if (!param.TRANS_DATE_MIN) param.TRANS_DATE_MIN = '';
        if (!param.TRANS_DATE_MAX) param.TRANS_DATE_MAX = '';
        if (!param.TRANS_PRICE_MIN) param.TRANS_PRICE_MIN = '';
        if (!param.TRANS_PRICE_MAX) param.TRANS_PRICE_MAX = '';
        if (!param.CORE_PROPERTY) param.CORE_PROPERTY = '';
        if (!param.NEED_INFO) param.NEED_INFO = '';
        if (!param.LEGAL_DESC) param.LEGAL_DESC = '%';
        if (!param.NOTES) param.NOTES = '%';
        if (!param.BUYER_ID) param.BUYER_ID = '';
        if (!param.SELLER_ID) param.SELLER_ID = '';
        if (!param.CURRENT_OWNER_ID) param.CURRENT_OWNER_ID = '';
        if (!param.EMPTY) param.EMPTY = '';
        if (!param.TYPE) param.TYPE = '';
        if (!param.STATE) param.STATE = '';
        if (!param.CATEGORY) param.CATEGORY = '';
    }

    if (page === 'statistics') {
        if (query.PROPERTY_ID) {
            param.PROPERTY_ID = query.PROPERTY_ID;
            param.PROPERTY_ID_FIRST = `${query.PROPERTY_ID},%`;
            param.PROPERTY_ID_LAST = `%,${query.PROPERTY_ID}`;
            param.PROPERTY_ID_MID = `%,${query.PROPERTY_ID},%`;
            where.push('CW' + id);
        }
        id++;
    }

    if (page === 'buyers') {
        const categories = (query.CATEGORY || '').split(',');
        for (let i = 0; i < 4; i++) {
            if (categories[i]) {
                param[`CATEGORY_${i + 1}`] = categories[i];
                param[`CATEGORY_${i + 1}_FIRST`] = `${categories[i]},%`;
                param[`CATEGORY_${i + 1}_LAST`] = `%,${categories[i]}`;
                param[`CATEGORY_${i + 1}_MID`] = `%,${categories[i]},%`;
                where.push('CW' + id);
            }
            id++;
        }

        const types = (query.TYPE || '').split(',');
        for (let i = 0; i < 2; i++) {
            if (types[i]) {
                param[`TYPE_${i + 1}`] = types[i];
                param[`TYPE_${i + 1}_FIRST`] = `${types[i]},%`;
                param[`TYPE_${i + 1}_LAST`] = `%,${types[i]}`;
                param[`TYPE_${i + 1}_MID`] = `%,${types[i]},%`;
                where.push('CW' + id);
            }
            id++;
        }

        const markets = (query.MARKET || '').split(',');
        for (let i = 0; i < 2; i++) {
            if (markets[i]) {
                param[`MARKET_${i + 1}`] = markets[i];
                param[`MARKET_${i + 1}_FIRST`] = `${markets[i]},%`;
                param[`MARKET_${i + 1}_LAST`] = `%,${markets[i]}`;
                param[`MARKET_${i + 1}_MID`] = `%,${markets[i]},%`;
                where.push('CW' + id);
            }
            id++;
        }
    }

    if (page === 'strivePipeline') {
        where.push('CW5');
    }
    if (page === 'strivePipelineByStatus') {
        where.push('CW6');
    }
    if (page === 'agent-commission') {
        where.push('CW6');
    }
    if(page === 'agent-commission-break-down'){
        where[0]='CW3';
    }

	if (query.order) {
		// Normalize: order may be array (order=NAME.asc&order=CITY.desc) or comma-separated string (order=NAME.asc,CITY.desc)
		const orders = Array.isArray(query.order)
			? query.order
			: String(query.order).split(',').map((s) => s.trim()).filter(Boolean);

		orders.forEach(x => {
			const [rawCode, direction] = x.split('.');
			// Legacy Peak/HSC Owner column used owner_links (client-only); map to sortable field.
			// PEAK_STATUS is the URL spelling of the HSC_STATUS column.
			const code = rawCode === 'owner_links' ? 'CURRENT_OWNER_NAME'
				: rawCode === 'PEAK_STATUS' ? 'HSC_STATUS'
				: rawCode;

            orderCols.some((column, i)=> {
				if (column === code) {
					order.push('CO'+(i*2+(direction === 'asc' ? 1 : 2)));
					return true;
				}
			});
		});

		// Keep properties without a listing agent on the last pages for both sort
		// directions. AGENT_NULL_FLAG (self-join column, non-null only when
		// AGENT_1.LAST_NAME is null) sits right before the agent name in the
		// candidate list, because Arcimedes applies ORDER BY candidates in their
		// fixed list positions regardless of the order they are sent in.
		if (orderCols === propertyOrder) {
			const agentIdx = propertyOrder.indexOf('AGENT_1_LAST_NAME');
			const flagIdx = propertyOrder.indexOf('AGENT_NULL_FLAG');
			const agentCoIdx = order.findIndex(
				co => co === 'CO' + (agentIdx * 2 + 1) || co === 'CO' + (agentIdx * 2 + 2)
			);
			if (agentCoIdx !== -1) {
				order.splice(agentCoIdx, 0, 'CO' + (flagIdx * 2 + 1));
			}

			const ownerIdx = propertyOrder.indexOf('CURRENT_OWNER_NAME');
			const ownerFlagIdx = propertyOrder.indexOf('CURRENT_OWNER_NULL_FLAG');
			const ownerCoIdx = order.findIndex(
				co => co === 'CO' + (ownerIdx * 2 + 1) || co === 'CO' + (ownerIdx * 2 + 2)
			);
			if (ownerCoIdx !== -1) {
				order.splice(ownerCoIdx, 0, 'CO' + (ownerFlagIdx * 2 + 1));
			}

			// The territory Status header sorts by its flags, as the client listed
			// them, then the rows without one by status and source; the newest
			// closing comes first, and no flag and no status stay last either way.
			const statusIdx = propertyOrder.indexOf('STATUS');
			const statusCoIdx = page === 'territories'
				? order.findIndex(co => co === 'CO' + (statusIdx * 2 + 1) || co === 'CO' + (statusIdx * 2 + 2))
				: -1;
			if (statusCoIdx !== -1) {
				const step = order[statusCoIdx] === 'CO' + (statusIdx * 2 + 1) ? 1 : 2;
				const ascendingOf = (code) => 'CO' + (propertyOrder.indexOf(code) * 2 + 1);
				order.splice(
					statusCoIdx + 1,
					0,
					'CO' + (propertyOrder.indexOf('SOURCE') * 2 + step),
					'CO' + (propertyOrder.indexOf('CLOSE_OF_ESCROW') * 2 + 2)
				);
				order.splice(
					statusCoIdx,
					0,
					ascendingOf('STATUS_FLAG_NONE'),
					'CO' + (propertyOrder.indexOf('STATUS_FLAG_RANK') * 2 + step),
					ascendingOf('STATUS_NULL_FLAG')
				);
			}
		}
	}
    /* CW remapping block removed -- propertyFilter order now matches SQL query order directly */
    /* buyer-breakdown CW remapping removed -- filter order now matches SQL */

    /* if(page=="properties"){
        let index = where.findIndex((e) => e=='CW80')
        if(index>=0){
            where[index]='CW77';
        }        
    }  */
    if(page=="team-member"){
        let index1 = where.findIndex((e) => e=='CW1')
        let index2 = where.findIndex((e) => e=='CW2')
        let index3 = where.findIndex((e) => e=='CW3')
        let index4 = where.findIndex((e) => e=='CW4')
      
        if(index1>=0){
            where[index1]='CW2';
        }  
        if(index2>=0){
            where[index2]='CW3';
        }
        if(index3>=0){
            where[index3]='CW4';
        }  
        if(index4>=0){
            where[index4]='CW5';
        }     
    }
    
    if(page == 'strivePipelineByStatus'){
        return {
            param,
            filter: {
                where
            }
        };
    } else {
        return {
            param,
            filter: {
                where
            },
            order,
        };
    }
};